import React, { useEffect, useState } from "react";
import { http } from "../../axios/axios";
import AdminHeader from "../../component/AdminHeader";

const API_BASE = import.meta.env.VITE_API_BASE || "http://localhost:8080";

const AdminHeroSection = () => {
  const cleanHeroData = (hero) => {
    return {
      badge: hero.badge?.trim() || "",
      title: hero.title?.trim() || "",
      highlightedTitle: hero.highlightedTitle?.trim() || "",
      description: hero.description?.trim() || "",

      primaryButton: {
        text: hero.primaryButton?.text?.trim() || "",
        link: hero.primaryButton?.link?.trim() || "",
      },

      secondaryButton: {
        text: hero.secondaryButton?.text?.trim() || "",
        link: hero.secondaryButton?.link?.trim() || "",
      },

      floatingStat: {
        value: hero.floatingStat?.value?.trim() || "",
        label: hero.floatingStat?.label?.trim() || "",
      },

      image: hero.image || "",
    };
  };

  const [form, setForm] = useState({
    hero: {
      badge: "",
      title: "",
      highlightedTitle: "",
      description: "",
      primaryButton: {
        text: "",
        link: "",
      },
      secondaryButton: {
        text: "",
        link: "",
      },
      floatingStat: {
        value: "",
        label: "",
      },
      image: "",
    },
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchHero();
  }, []);

  const fetchHero = async () => {
    try {
      setFetching(true);

      const res = await http.get("/about/hero");
      const data = res.data.data;

      if (!data) return;

      setForm({
        hero: {
          badge: data.badge || "",
          title: data.title || "",
          highlightedTitle: data.highlightedTitle || "",
          description: data.description || "",

          primaryButton: {
            text: data.primaryButton?.text || "",
            link: data.primaryButton?.link || "",
          },

          secondaryButton: {
            text: data.secondaryButton?.text || "",
            link: data.secondaryButton?.link || "",
          },

          floatingStat: {
            value: data.floatingStat?.value || "",
            label: data.floatingStat?.label || "",
          },

          image: data.image || "",
        },
      });

      if (data.image) {
        setPreview(`${API_BASE}/${data.image}`);
      }
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed loading hero",
      );
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    const keys = name.split(".");

    setForm((prev) => {
      const updated = structuredClone(prev);

      let current = updated;

      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;

      return updated;
    });
  };

  const handleImage = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Only image files allowed");
      return;
    }

    setError("");
    setSelectedFile(file);

    setPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError("");

      const cleanedHero = cleanHeroData(form.hero);

      const formData = new FormData();

      if (selectedFile) {
        formData.append("image", selectedFile);
      }

      formData.append("hero", JSON.stringify(cleanedHero));

      const res = await http.put("/about/hero", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      alert("Hero Saved Successfully");

      console.log(res.data);

      fetchHero();
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Something went wrong",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 pt-0 pb-6 space-y-6">
      <h2 className="text-2xl font-bold">Hero Section</h2>

      {error && (
        <div className="bg-red-100 text-red-600 p-3 rounded">{error}</div>
      )}

      {fetching && <p>Loading hero data...</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <input
          name="hero.badge"
          value={form.hero.badge}
          onChange={handleChange}
          placeholder="Badge Text (span)"
          className="border px-3 py-2 rounded"
        />

        <input
          name="hero.title"
          value={form.hero.title}
          onChange={handleChange}
          placeholder="Main Title (h1)"
          className="border px-3 py-2 rounded"
        />

        <input
          name="hero.highlightedTitle"
          value={form.hero.highlightedTitle}
          onChange={handleChange}
          placeholder="Highlighted Title (h1 span)"
          className="border px-3 py-2 rounded"
        />

        <textarea
          name="hero.description"
          value={form.hero.description}
          onChange={handleChange}
          placeholder="Description Text (p)"
          className="border px-3 py-2 rounded md:col-span-2"
        />

        <input
          name="hero.primaryButton.text"
          value={form.hero.primaryButton.text}
          onChange={handleChange}
          placeholder="Primary Button Text (a/button)"
          className="border px-3 py-2 rounded"
        />

        <input
          name="hero.primaryButton.link"
          value={form.hero.primaryButton.link}
          onChange={handleChange}
          placeholder="Primary Button Link (href)"
          className="border px-3 py-2 rounded"
        />

        <input
          name="hero.secondaryButton.text"
          value={form.hero.secondaryButton.text}
          onChange={handleChange}
          placeholder="Secondary Button Text (a/button)"
          className="border px-3 py-2 rounded"
        />

        <input
          name="hero.secondaryButton.link"
          value={form.hero.secondaryButton.link}
          onChange={handleChange}
          placeholder="Secondary Button Link (href)"
          className="border px-3 py-2 rounded"
        />

        <input
          name="hero.floatingStat.value"
          value={form.hero.floatingStat.value}
          onChange={handleChange}
          placeholder="Floating Number (h4)"
          className="border px-3 py-2 rounded"
        />

        <input
          name="hero.floatingStat.label"
          value={form.hero.floatingStat.label}
          onChange={handleChange}
          placeholder="Floating Label (p)"
          className="border px-3 py-2 rounded"
        />

        <input
          type="file"
          accept="image/*"
          onChange={handleImage}
          className="border px-3 py-2 rounded md:col-span-2"
        />

        {preview && (
          <img
            src={preview}
            className="w-64 h-40 object-cover rounded border md:col-span-2"
          />
        )}
      </div>

      <button
        onClick={handleSubmit}
        disabled={loading}
        className="bg-blue-600 text-white px-6 py-2 rounded"
      >
        {loading ? "Saving..." : "Save Hero"}
      </button>
    </div>
  );
};

const AdminStatsSection = () => {
  const [form, setForm] = useState({
    stats: [
      { value: "", label: "" },
      { value: "", label: "" },
      { value: "", label: "" },
      { value: "", label: "" },
    ],
  });

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setFetching(true);

      const res = await http.get("/about/stats");

      const data = res.data.data;

      if (!data) return;

      setForm({
        stats: data,
      });
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to load stats",
      );
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (index, field, value) => {
    setForm((prev) => {
      const updated = structuredClone(prev);
      updated.stats[index][field] = value;
      return updated;
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError("");

      const hasEmpty = form.stats.some(
        (item) => !item.value.trim() || !item.label.trim(),
      );

      if (hasEmpty) {
        setError("All stats fields (value & label) are required");
        setLoading(false);
        return;
      }

      const payload = {
        stats: form.stats,
      };

      await http.put("/about/stats", payload);

      alert("Stats Saved Successfully!");

      fetchStats();
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to save stats",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <h2 className="text-2xl bold">Stats Section</h2>

      {error && (
        <div className="bg-red-100 text-red-600 p-3 rounded-lg">{error}</div>
      )}

      {fetching && <p>Loading stats...</p>}

      <div className="space-y-4">
        {form.stats.map((item, index) => (
          <div key={index} className="flex gap-3">
            <input
              type="text"
              placeholder={`Stat Value (data) ${index + 1}`}
              value={item.value}
              onChange={(e) => handleChange(index, "value", e.target.value)}
              className="border rounded-lg px-3 py-2 w-1/2"
            />

            <input
              type="text"
              placeholder={`Stat Label (p) ${index + 1}`}
              value={item.label}
              onChange={(e) => handleChange(index, "label", e.target.value)}
              className="border rounded-lg px-3 py-2 w-1/2"
            />
          </div>
        ))}
      </div>

      <button
        onClick={handleSubmit}
        disabled={loading}
        className="
          bg-blue-600
          text-white
          px-6
          py-2
          rounded-lg
          hover:bg-blue-700
          disabled:opacity-50
        "
      >
        {loading ? "Saving..." : "Save Changes"}
      </button>
    </div>
  );
};

const AdminStorySection = () => {
  const cleanStoryData = (story) => {
    return {
      badge: story.badge?.trim(),
      title: story.title?.trim(),
      description: story.description?.trim(),

      image: story.image,

      contentTitle: story.contentTitle?.trim(),

      paragraphOne: story.paragraphOne?.trim(),
      paragraphTwo: story.paragraphTwo?.trim(),

      highlights: story.highlights.map((h) => h.trim()).filter(Boolean),
    };
  };

  const [form, setForm] = useState({
    story: {
      badge: "",
      title: "",
      description: "",
      image: "",

      contentTitle: "",

      paragraphOne: "",
      paragraphTwo: "",

      highlights: ["", "", "", ""],
    },
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    const keys = name.split(".");

    setForm((prev) => {
      const updated = structuredClone(prev);

      let current = updated;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;

      return updated;
    });
  };

  const handleHighlightChange = (index, value) => {
    setForm((prev) => {
      const updated = structuredClone(prev);
      updated.story.highlights[index] = value;
      return updated;
    });
  };

  const handleImage = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Only image files allowed");
      return;
    }

    setError("");
    setSelectedFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError("");

      const cleanedStory = cleanStoryData(form.story);

      const formData = new FormData();

      if (selectedFile) {
        formData.append("image", selectedFile);
      }

      formData.append("story", JSON.stringify(cleanedStory));
      for (let pair of formData.entries()) {
        console.log(pair[0], pair[1]);
      }
      const res = await http.put("/about/story", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      console.log("Story Response:", res.data);

      alert("Story Saved Successfully!");
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Something went wrong",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStory();
  }, []);

  const fetchStory = async () => {
    try {
      const res = await http.get("/about/story");

      const data = res.data.data;

      if (!data) return;

      setForm({
        story: {
          badge: data.badge || "",
          title: data.title || "",
          description: data.description || "",
          image: data.image || "",

          contentTitle: data.contentTitle || "",

          paragraphOne: data.paragraphOne || "",
          paragraphTwo: data.paragraphTwo || "",

          highlights: data.highlights?.length
            ? data.highlights
            : ["", "", "", ""],
        },
      });

      if (data.image) {
        setPreview(`${API_BASE}/${data.image}`);
      }
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to load story",
      );
    }
  };
  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <h2 className="text-2xl font-bold">Story Section</h2>

      {error && (
        <div className="bg-red-100 text-red-600 p-3 rounded-lg">{error}</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <input
          name="story.badge"
          placeholder="Section Badge (span)"
          value={form.story.badge}
          onChange={handleChange}
          className="border px-3 py-2 rounded"
        />

        <input
          name="story.title"
          placeholder="Section Title (h2)"
          value={form.story.title}
          onChange={handleChange}
          className="border px-3 py-2 rounded"
        />

        <textarea
          name="story.description"
          placeholder="Section Description (p)"
          value={form.story.description}
          onChange={handleChange}
          className="border px-3 py-2 rounded md:col-span-2"
        />

        <input
          name="story.contentTitle"
          placeholder="Story Heading (h2)"
          value={form.story.contentTitle}
          onChange={handleChange}
          className="border px-3 py-2 rounded md:col-span-2"
        />
        <textarea
          name="story.paragraphOne"
          placeholder="First Story Paragraph (p)"
          value={form.story.paragraphOne}
          onChange={handleChange}
          className="border px-3 py-2 rounded md:col-span-2 min-h-[120px]"
        />
        <textarea
          name="story.paragraphTwo"
          placeholder="Second Story Paragraph (p)"
          value={form.story.paragraphTwo}
          onChange={handleChange}
          className="border px-3 py-2 rounded md:col-span-2 min-h-[120px]"
        />
        {form.story.highlights.map((item, index) => (
          <input
            key={index}
            value={item}
            placeholder={`Highlight ${index + 1} (li)`}
            onChange={(e) => handleHighlightChange(index, e.target.value)}
            className="border px-3 py-2 rounded"
          />
        ))}

        <input
          type="file"
          accept="image/*"
          onChange={handleImage}
          className="border px-3 py-2 rounded md:col-span-2"
        />

        {preview && (
          <img
            src={preview}
            alt="preview"
            className="w-64 h-40 object-cover rounded border md:col-span-2"
          />
        )}
      </div>

      <button
        onClick={handleSubmit}
        disabled={loading}
        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
      >
        {loading ? "Saving..." : "Save Story"}
      </button>
    </div>
  );
};

const AdminServicesSection = () => {
  const cleanServicesData = (services) => {
    return {
      badge: services.badge?.trim() || "",
      title: services.title?.trim() || "",
      description: services.description?.trim() || "",

      items: services.items.map((item) => ({
        title: item.title?.trim() || "",
        description: item.description?.trim() || "",
      })),
    };
  };

  const [form, setForm] = useState({
    services: {
      badge: "",
      title: "",
      description: "",
      items: [],
    },
    sections: [
      { badge: "", title: "", description: "", items: [] },
      { badge: "", title: "", description: "", items: [] },
    ],
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;

    const keys = name.split(".");

    setForm((prev) => {
      const updated = structuredClone(prev);

      let current = updated;

      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;

      return updated;
    });
  };

  const handleItemChange = (index, field, value) => {
    setForm((prev) => {
      const updated = structuredClone(prev);

      updated.services.items[index][field] = value;

      return updated;
    });
  };
  const updateSection = (sectionIndex, field, value) => {
    setForm((prev) => ({
      ...prev,
      sections: prev.sections.map((section, index) => index === sectionIndex ? { ...section, [field]: value } : section),
    }));
  };
  const updateSectionItem = (sectionIndex, itemIndex, field, value) => {
    setForm((prev) => ({
      ...prev,
      sections: prev.sections.map((section, index) => index !== sectionIndex ? section : { ...section, items: section.items.map((item, itemIndexValue) => itemIndexValue === itemIndex ? { ...item, [field]: value } : item) }),
    }));
  };
  const addSectionItem = (sectionIndex) => updateSection(sectionIndex, "items", [...form.sections[sectionIndex].items, { title: "", description: "" }]);
  const removeSectionItem = (sectionIndex, itemIndex) => updateSection(sectionIndex, "items", form.sections[sectionIndex].items.filter((_, index) => index !== itemIndex));
  const addServiceItem = () => {
    setForm((prev) => ({
      ...prev,

      services: {
        ...prev.services,

        items: [
          ...prev.services.items,
          {
            title: "",
            description: "",
          },
        ],
      },
    }));
  };
  const removeServiceItem = (index) => {
    setForm((prev) => ({
      ...prev,

      services: {
        ...prev.services,

        items: prev.services.items.filter((_, i) => i !== index),
      },
    }));
  };
  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError("");

      const cleaned = {
        ...cleanServicesData(form.sections[0] || form.services),
        sections: form.sections.map((section) => cleanServicesData(section)),
      };

      const res = await http.put("/about/services", {
        services: cleaned,
      });

      console.log("Services Response", res.data);

      alert("Services Saved Successfully");
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Something went wrong",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const res = await http.get("/about/services");

      const data = res.data.data;

      if (!data) return;

      setForm({
        services: {
          badge: data.badge || "",
          title: data.title || "",
          description: data.description || "",
          items: data.items || [],
        },
        sections: data.sections?.length ? data.sections : [{ badge: data.badge || "", title: data.title || "", description: data.description || "", items: data.items || [] }, { badge: "", title: "", description: "", items: [] }],
      });
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to load services",
      );
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <h2 className="text-2xl font-bold">Services Sections</h2>

      {error && (
        <div className="bg-red-100 text-red-600 p-3 rounded">{error}</div>
      )}

      <div className="grid grid-cols-1 gap-8">
        {form.sections.map((section, sectionIndex) => <section key={sectionIndex} className="rounded-xl border p-5 space-y-4">
          <h3 className="text-xl font-bold">Services Section {sectionIndex + 1}</h3>
          <input placeholder="Section Badge (span)" value={section.badge} onChange={(e) => updateSection(sectionIndex, "badge", e.target.value)} className="border px-3 py-2 rounded w-full" />
          <input placeholder="Section Title (h2)" value={section.title} onChange={(e) => updateSection(sectionIndex, "title", e.target.value)} className="border px-3 py-2 rounded w-full" />
          <textarea placeholder="Section Description (p)" value={section.description} onChange={(e) => updateSection(sectionIndex, "description", e.target.value)} className="border px-3 py-2 rounded w-full" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {section.items.map((item, itemIndex) => <div key={itemIndex} className="relative rounded-xl border p-4 space-y-3"><button type="button" onClick={() => removeSectionItem(sectionIndex, itemIndex)} className="absolute right-3 top-3 text-red-600">Remove</button><h4 className="font-semibold">Card {itemIndex + 1}</h4><input placeholder="Service Title" value={item.title} onChange={(e) => updateSectionItem(sectionIndex, itemIndex, "title", e.target.value)} className="border px-3 py-2 rounded w-full" /><textarea placeholder="Service Description" value={item.description} onChange={(e) => updateSectionItem(sectionIndex, itemIndex, "description", e.target.value)} className="border px-3 py-2 rounded w-full" /></div>)}
          </div>
          <button type="button" onClick={() => addSectionItem(sectionIndex)} className="border border-blue-600 text-blue-600 px-5 py-2 rounded-lg">+ Add Service Card</button>
        </section>)}
      </div>
      <div className="hidden">
        <input
          name="services.badge"
          placeholder="Section Badge (span)"
          value={form.services.badge}
          onChange={handleChange}
          className="border px-3 py-2 rounded"
        />
        <input
          name="services.title"
          placeholder="Section Title (h2)"
          value={form.services.title}
          onChange={handleChange}
          className="border px-3 py-2 rounded"
        />

        <textarea
          name="services.description"
          placeholder="Section Description (p)"
          value={form.services.description}
          onChange={handleChange}
          className="border px-3 py-2 rounded md:col-span-2"
        />

        {form.services.items.map((item, index) => (
          <div
            key={index}
            className="md:col-span-2
border
rounded-xl
p-4
space-y-3
relative
"
          >
            <button
              type="button"
              onClick={() => removeServiceItem(index)}
              className="
absolute
right-3
top-3
text-red-600
"
            >
              Remove
            </button>

            <h3 className="font-semibold">Service {index + 1}</h3>

            <input
              placeholder={`Service Title ${index + 1} (h3)`}
              value={item.title}
              onChange={(e) => handleItemChange(index, "title", e.target.value)}
              className="border px-3 py-2 rounded w-full"
            />

            <textarea
              placeholder={`Service Description ${index + 1} (p)`}
              value={item.description}
              onChange={(e) =>
                handleItemChange(index, "description", e.target.value)
              }
              className="
border 
px-3 
py-2 
rounded 
w-full
"
            />
          </div>
        ))}
      </div>
      <div className="flex items-center gap-5 justify-between">
        <button
          type="button"
          onClick={addServiceItem}
          className="hidden
border
border-blue-600
text-blue-600
px-5
py-2
rounded-lg
"
        >
          + Add Service
        </button>

        <button
          onClick={handleSubmit}
          disabled={loading}
          className="
bg-blue-600
text-white
px-6
py-2
rounded
"
        >
          {loading ? "Saving..." : "Save Services"}
        </button>
      </div>
    </div>
  );
};

const AdminIndustriesSection = () => {
  const cleanIndustriesData = (industries) => {
    return {
      badge: industries.badge?.trim() || "",
      title: industries.title?.trim() || "",
      description: industries.description?.trim() || "",

      items: industries.items
        .map((item) => ({
          title: item.title?.trim() || "",
        }))
        .filter((item) => item.title),
    };
  };

  const [form, setForm] = useState({
    industries: {
      badge: "",
      title: "",
      description: "",
      items: [],
    },
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    const keys = name.split(".");
    setForm((prev) => {
      const updated = structuredClone(prev);
      let current = updated;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return updated;
    });
  };

  const handleItemChange = (index, value) => {
    setForm((prev) => {
      const updated = structuredClone(prev);
      updated.industries.items[index].title = value;
      return updated;
    });
  };

  const addIndustry = () => {
    setForm((prev) => ({
      ...prev,

      industries: {
        ...prev.industries,

        items: [
          ...prev.industries.items,

          {
            title: "",
          },
        ],
      },
    }));
  };

  const removeIndustry = (index) => {
    setForm((prev) => ({
      ...prev,

      industries: {
        ...prev.industries,

        items: prev.industries.items.filter((_, i) => i !== index),
      },
    }));
  };
  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError("");

      const cleaned = cleanIndustriesData(form.industries);

      console.log("Industries Response", cleaned);
      const res = await http.put("/about/industries", {
        industries: cleaned,
      });

      console.log("Industries Response", res.data);

      alert("Industries Saved Successfully!");
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Something went wrong",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIndustries();
  }, []);

  const fetchIndustries = async () => {
    try {
      const res = await http.get("/about/industries");

      const data = res.data.data;

      if (!data) return;

      setForm({
        industries: {
          badge: data.badge || "",
          title: data.title || "",
          description: data.description || "",
          items: data.items || [],
        },
      });
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.message ||
        "Failed to load industries",
      );
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <h2 className="text-2xl font-bold">Industries Section</h2>

      {error && (
        <div
          className="
bg-red-100
text-red-600
p-3
rounded
"
        >
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <input
          name="industries.badge"
          placeholder="Section Badge (span)"
          value={form.industries.badge}
          onChange={handleChange}
          className="
border
px-3
py-2
rounded
"
        />
        <input
          name="industries.title"
          placeholder="Section Title (h2)"
          value={form.industries.title}
          onChange={handleChange}
          className="
border
px-3
py-2
rounded
"
        />
        <textarea
          name="industries.description"
          placeholder="Section Description (p)"
          value={form.industries.description}
          onChange={handleChange}
          className="
border
px-3
py-2
rounded
md:col-span-2
"
        />
        {form.industries.items.map((item, index) => (
          <div
            key={index}
            className="
md:col-span-2
border
rounded-xl
p-4
relative
"
          >
            <button
              type="button"
              onClick={() => removeIndustry(index)}
              className="
absolute
right-3
top-3
text-red-600
"
            >
              Remove
            </button>

            <h3 className="font-semibold mb-3">Industry {index + 1}</h3>

            <input
              value={item.title}
              placeholder={`Industry Name ${index + 1} (h3)`}
              onChange={(e) => handleItemChange(index, e.target.value)}
              className="
border
px-3
py-2
rounded
w-full
"
            />
          </div>
        ))}
      </div>
      <div className="flex items-center gap-5 justify-between ">
        <button
          type="button"
          onClick={addIndustry}
          className="
border
border-blue-600
text-blue-600
px-5
py-2
rounded-lg
"
        >
          + Add Industry
        </button>

        <button
          onClick={handleSubmit}
          disabled={loading}
          className="
bg-blue-600
text-white
px-6
py-2
rounded
"
        >
          {loading ? "Saving..." : "Save Industries"}
        </button>
      </div>
    </div>
  );
};

const AdminClosingSection = () => {
  const cleanClosingData = (closingSection) => {
    return {
      badge: closingSection.badge?.trim() || "",
      title: closingSection.title?.trim() || "",
      highlightedTitle: closingSection.highlightedTitle?.trim() || "",
      description: closingSection.description?.trim() || "",
      points: closingSection.points.map((item) => item.trim()).filter(Boolean),
      button: {
        text: closingSection.button.text?.trim() || "",
        link: closingSection.button.link?.trim() || "",
      },
      footerText: closingSection.footerText?.trim() || "",
      cardImage: {
        alt:
          closingSection.cardImage?.alt?.trim() ||
          "Suraksha 24X7 Free Security Consultation",
      },
      cardButtonText:
        closingSection.cardButtonText?.trim() ||
        "Schedule a Free Consultation",
      cardButtonLink: closingSection.cardButtonLink?.trim() || "/contact",
      showCard: closingSection.showCard !== false,
    };
  };

  const [form, setForm] = useState({
    closingSection: {
      badge: "",
      title: "",
      highlightedTitle: "",
      description: "",

      points: [],

      button: {
        text: "",
        link: "",
      },

      footerText: "",
      cardImage: {
        url: "",
        publicId: "",
        alt: "Suraksha 24X7 Free Security Consultation",
      },
      cardButtonText: "Schedule a Free Consultation",
      cardButtonLink: "/contact",
      showCard: true,
    },
  });

  const [selectedCardImage, setSelectedCardImage] = useState(null);
  const [cardImagePreview, setCardImagePreview] = useState("");
  const [removeCardImage, setRemoveCardImage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    const keys = name.split(".");

    setForm((prev) => {
      const updated = structuredClone(prev);

      let current = updated;

      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = type === "checkbox" ? checked : value;

      return updated;
    });
  };

  const handlePointChange = (index, value) => {
    setForm((prev) => {
      const updated = structuredClone(prev);

      updated.closingSection.points[index] = value;

      return updated;
    });
  };

  const addPoint = () => {
    setForm((prev) => ({
      ...prev,

      closingSection: {
        ...prev.closingSection,

        points: [...prev.closingSection.points, ""],
      },
    }));
  };

  const handleCardImage = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("CTA card image must be an image file");
      return;
    }

    setError("");
    setSelectedCardImage(file);
    setCardImagePreview(URL.createObjectURL(file));
    setRemoveCardImage(false);
  };

  const handleRemoveCardImage = () => {
    setSelectedCardImage(null);
    setCardImagePreview("");
    setRemoveCardImage(true);
  };

  const removePoint = (index) => {
    setForm((prev) => ({
      ...prev,

      closingSection: {
        ...prev.closingSection,

        points: prev.closingSection.points.filter((_, i) => i !== index),
      },
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError("");

      const cleaned = cleanClosingData(form.closingSection);
      const formData = new FormData();

      formData.append("closingSection", JSON.stringify(cleaned));
      formData.append("removeCardImage", String(removeCardImage));
      if (selectedCardImage) {
        formData.append("closingCTACardImage", selectedCardImage);
      }

      await http.put("/about/closing-section", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      alert("Closing Section Saved Successfully!");
      setSelectedCardImage(null);
      setRemoveCardImage(false);
      await fetchClosing();
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Something went wrong",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClosing();
  }, []);

  const fetchClosing = async () => {
    try {
      const res = await http.get("/about/closing-section");

      const data = res.data.data;

      if (!data) return;

      setForm({
        closingSection: {
          badge: data.badge || "",
          title: data.title || "",
          highlightedTitle: data.highlightedTitle || "",
          description: data.description || "",

          points: data.points || [],

          button: {
            text: data.button?.text || "",
            link: data.button?.link || "",
          },

          footerText: data.footerText || "",
          cardImage: {
            url: data.cardImage?.url || "",
            publicId: data.cardImage?.publicId || "",
            alt:
              data.cardImage?.alt ||
              "Suraksha 24X7 Free Security Consultation",
          },
          cardButtonText:
            data.cardButtonText || "Schedule a Free Consultation",
          cardButtonLink: data.cardButtonLink || "/contact",
          showCard: data.showCard !== false,
        },
      });
      setCardImagePreview(data.cardImage?.url || "");
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.message ||
        "Failed to load closing section",
      );
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <h2 className="text-2xl font-bold">Closing CTA Section</h2>

      {error && (
        <div
          className="
          bg-red-100
          text-red-600
          p-3
          rounded
        "
        >
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <input
          name="closingSection.badge"
          placeholder="Badge Text (span)"
          value={form.closingSection.badge}
          onChange={handleChange}
          className="
          border
          px-3
          py-2
          rounded
          "
        />

        <input
          name="closingSection.title"
          placeholder="Main Title (h2)"
          value={form.closingSection.title}
          onChange={handleChange}
          className="
          border
          px-3
          py-2
          rounded
          "
        />

        <input
          name="closingSection.highlightedTitle"
          placeholder="Highlighted Title (h2 span)"
          value={form.closingSection.highlightedTitle}
          onChange={handleChange}
          className="
          border
          px-3
          py-2
          rounded
          "
        />

        <textarea
          name="closingSection.description"
          placeholder="Description Text (p)"
          onChange={handleChange}
          value={form.closingSection.description}
          className="
          border
          px-3
          py-2
          rounded
          md:col-span-2
          min-h-[120px]
          "
        />

        {/* POINTS */}

        <div
          className="
          md:col-span-2
          border
          rounded-xl
          p-4
          space-y-3
        "
        >
          <h3 className="font-semibold">Points</h3>

          {form.closingSection.points.map((point, index) => (
            <div key={index} className="flex gap-3">
              <input
                value={point}
                placeholder={`Trust Point ${index + 1} (li)`}
                onChange={(e) => handlePointChange(index, e.target.value)}
                className="
                  border
                  px-3
                  py-2
                  rounded
                  flex-1
                  "
              />

              <button
                type="button"
                onClick={() => removePoint(index)}
                className="
                  text-red-600
                  "
              >
                Remove
              </button>
            </div>
          ))}

          <button
            type="button"
            onClick={addPoint}
            className="
            border
            border-blue-600
            text-blue-600
            px-4
            py-2
            rounded
            "
          >
            + Add Point
          </button>
        </div>

        <input
          name="closingSection.button.text"
          placeholder="CTA Button Text (a/link)"
          value={form.closingSection.button.text}
          onChange={handleChange}
          className="
          border
          px-3
          py-2
          rounded
          "
        />

        <input
          name="closingSection.button.link"
          placeholder="CTA Button URL (href)"
          value={form.closingSection.button.link}
          onChange={handleChange}
          className="
          border
          px-3
          py-2
          rounded
          "
        />

        <textarea
          name="closingSection.footerText"
          placeholder="Bottom Support Text (p)"
          value={form.closingSection.footerText}
          onChange={handleChange}
          className="
          border
          px-3
          py-2
          rounded
          md:col-span-2
          "
        />

        <div className="space-y-4 rounded-xl border p-4 md:col-span-2">
          <h3 className="font-semibold">Closing CTA Card</h3>

          {cardImagePreview && (
            <img
              src={cardImagePreview}
              alt={
                form.closingSection.cardImage.alt ||
                "Closing CTA card preview"
              }
              className="aspect-4/3 w-full max-w-md rounded-lg border border-slate-200 object-cover"
            />
          )}

          <div className="flex flex-wrap items-center gap-3">
            <label className="cursor-pointer rounded border border-blue-600 px-4 py-2 font-medium text-blue-600">
              Choose Image
              <input
                type="file"
                accept="image/*"
                onChange={handleCardImage}
                className="hidden"
              />
            </label>

            {cardImagePreview && (
              <button
                type="button"
                onClick={handleRemoveCardImage}
                className="rounded border border-red-300 px-4 py-2 font-medium text-red-600"
              >
                Remove Image
              </button>
            )}
          </div>

          <input
            name="closingSection.cardImage.alt"
            value={form.closingSection.cardImage.alt}
            onChange={handleChange}
            placeholder="Image Alt Text"
            className="w-full rounded border px-3 py-2"
          />

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <input
              name="closingSection.cardButtonText"
              value={form.closingSection.cardButtonText}
              onChange={handleChange}
              placeholder="Card Button Text"
              className="rounded border px-3 py-2"
            />

            <input
              name="closingSection.cardButtonLink"
              value={form.closingSection.cardButtonLink}
              onChange={handleChange}
              placeholder="Card Button Link"
              className="rounded border px-3 py-2"
            />
          </div>

          <label className="flex items-center gap-3 font-medium text-slate-700">
            <input
              type="checkbox"
              name="closingSection.showCard"
              checked={form.closingSection.showCard}
              onChange={handleChange}
              className="h-4 w-4 accent-blue-600"
            />
            Active / Show Card
          </label>
        </div>
      </div>

      <button
        onClick={handleSubmit}
        disabled={loading}
        className="
        bg-blue-600
        text-white
        px-6
        py-2
        rounded
        hover:bg-blue-700
        disabled:opacity-50
        "
      >
        {loading ? "Saving..." : "Save Closing Section"}
      </button>
    </div>
  );
};

const AdminAboutMeta = () => {
  const [meta, setMeta] = useState({
    metaTitle: "",
    metaDescription: "",
    metaTags: "",
    schemaScript: "",
  });

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchAdminMeta();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setMeta((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError("");

      const payload = {
        ...meta,
        metaTags: meta.metaTags
          .split(",")
          .map((tag) => tag.trim())
          .filter(Boolean),
      };

      await http.put("/about/meta", {
        meta: payload,
      });
      console.log("Meta Payload:", payload);

      alert("Meta Information Saved Successfully!");
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Something went wrong",
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminMeta = async () => {
    try {
      setFetching(true);

      const res = await http.get("/about/meta");

      const data = res.data.data;

      if (!data) return;

      setMeta({
        metaTitle: data.metaTitle || "",
        metaDescription: data.metaDescription || "",
        metaTags: Array.isArray(data.metaTags) ? data.metaTags.join(", ") : "",
        schemaScript: data.schemaScript || "",
      });
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.message ||
        "Failed to load meta information",
      );
    } finally {
      setFetching(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <h2 className="text-2xl font-bold">About Page SEO</h2>

      {error && (
        <div className="bg-red-100 text-red-600 p-3 rounded">{error}</div>
      )}

      {fetching && <p>Loading meta information...</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Meta Title */}
        <div>
          <label className="block text-sm font-medium mb-2">Meta Title</label>

          <input
            type="text"
            name="metaTitle"
            value={meta.metaTitle}
            onChange={handleChange}
            placeholder="Enter Meta Title"
            className="w-full border px-3 py-2 rounded"
          />
        </div>

        {/* Meta Tags */}
        <div>
          <label className="block text-sm font-medium mb-2">Meta Tags</label>

          <input
            type="text"
            name="metaTags"
            value={meta.metaTags}
            onChange={handleChange}
            placeholder="cctv, security, surveillance, networking"
            className="w-full border px-3 py-2 rounded"
          />

          <p className="text-xs text-gray-500 mt-1">
            Separate tags with commas.
          </p>
        </div>

        {/* Meta Description */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-2">
            Meta Description
          </label>

          <textarea
            name="metaDescription"
            value={meta.metaDescription}
            onChange={handleChange}
            rows={4}
            placeholder="Enter Meta Description"
            className="w-full border px-3 py-2 rounded"
          />
        </div>

        {/* Schema Script */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-2">
            Schema Script (JSON-LD)
          </label>

          <textarea
            name="schemaScript"
            value={meta.schemaScript}
            onChange={handleChange}
            rows={10}
            placeholder='{"@context":"https://schema.org","@type":"Organization"}'
            className="w-full border px-3 py-2 rounded font-mono text-sm"
          />

          <p className="text-xs text-gray-500 mt-1">
            Paste valid JSON-LD schema here.
          </p>
        </div>
      </div>

      <button
        onClick={handleSubmit}
        disabled={loading}
        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
      >
        {loading ? "Saving..." : "Save Meta Section"}
      </button>
    </div>
  );
};

const AdminAboutPage = () => {
  return (
    <>
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        <AdminHeader title="About Page" />
      </div>
      <AdminHeroSection />
      <AdminStatsSection />
      <AdminStorySection />
      <AdminServicesSection />
      <AdminIndustriesSection />
      <AdminClosingSection />
      <AdminAboutMeta />
    </>
  );
};

export default AdminAboutPage;
